import React, { useState } from "react";
import { Check, CheckCircle2, Clock, Copy, CreditCard, Loader2, ThumbsUp } from "lucide-react";

import { captureEvent } from "utils/posthog";
import { COPY } from "./copy";
import { mockDelay } from "./setupState";

/**
 * Phase 2 — revenue read from Stripe, visits from our own script.
 *
 * Live mode is honest about not being built: a coming-soon panel that captures
 * demand instead of pretending. Demo mode walks the intended steps so the shape
 * of the finished flow can be shown in a sales conversation.
 */

// ── Live: coming soon + interest capture ─────────────────────────────────────

export function RevenueStackComingSoon() {
    const c = COPY.revenueStack.comingSoon;
    const [upvoted, setUpvoted] = useState(false);
    const [useCase, setUseCase] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const upvote = () => {
        if (upvoted) return;
        setUpvoted(true);
        // PostHog is the existing demand signal in this codebase — no new backend.
        captureEvent("feature_upvoted", { feature: "revenue_attribution_stripe", surface: "setup_wizard" });
    };

    const submit = async () => {
        if (submitting || !useCase.trim()) return;
        setSubmitting(true);
        captureEvent("feature_requested", {
            feature: "revenue_attribution_stripe",
            surface: "setup_wizard",
            use_case: useCase.trim()
        });
        await mockDelay(600);
        setSubmitting(false);
        setSubmitted(true);
    };

    return (
        <div>
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50/70 px-4 py-3.5">
                <Clock className="mt-0.5 h-4 w-4 flex-none text-gray-400" strokeWidth={2} />
                <p className="text-[12.5px] leading-relaxed text-gray-600">{c.body}</p>
            </div>

            <div className="mt-5 rounded-lg border border-geekblue-200 bg-geekblue-100/40 px-4 py-3.5">
                <p className="text-[13px] font-semibold text-gray-900">{c.interestHeading}</p>
                <p className="mt-1 text-[12.5px] leading-relaxed text-gray-600">{c.interestBody}</p>

                {submitted ? (
                    <p className="mt-3 inline-flex items-center gap-2 text-[12.5px] font-medium text-emerald-700">
                        <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
                        {c.thanks}
                    </p>
                ) : (
                    <>
                        <button
                            type="button"
                            onClick={upvote}
                            disabled={upvoted}
                            className={`mt-3 inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[12px] font-medium transition-colors ${
                                upvoted
                                    ? "cursor-default border-emerald-200 bg-emerald-50 text-emerald-700"
                                    : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                            }`}>
                            {upvoted ? (
                                <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                            ) : (
                                <ThumbsUp className="h-3.5 w-3.5" strokeWidth={2} />
                            )}
                            {upvoted ? c.upvoted : c.upvote}
                        </button>

                        <textarea
                            value={useCase}
                            onChange={(e) => setUseCase(e.target.value)}
                            rows={3}
                            placeholder={c.useCasePlaceholder}
                            className="mt-3 w-full resize-y rounded-lg border border-gray-200 px-3 py-2 text-[12.5px] text-gray-900 focus:border-geekblue-400 focus:outline-none"
                        />

                        <div className="mt-2 flex flex-wrap items-center gap-3">
                            <button
                                type="button"
                                onClick={submit}
                                disabled={submitting || !useCase.trim()}
                                className="inline-flex items-center gap-2 rounded-lg bg-geekblue-600 px-3.5 py-2 text-[12.5px] font-medium text-white transition-colors hover:bg-geekblue-700 disabled:cursor-not-allowed disabled:opacity-40">
                                {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                                {submitting ? c.submitting : c.submit}
                            </button>
                            {!useCase.trim() && <span className="text-[11.5px] text-gray-400">{c.needUseCase}</span>}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

// ── Demo: the intended steps ─────────────────────────────────────────────────

const DEMO_SNIPPET = `<script async src="https://cdn.thrivestack.ai/ts.js"
  data-workspace="ws_8f21c4"></script>`;

export function RevenueStackScriptStep() {
    const c = COPY.revenueStack.script;
    const [copied, setCopied] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [verified, setVerified] = useState(false);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(DEMO_SNIPPET);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {
            /* clipboard blocked — the snippet is selectable on screen */
        }
    };

    const verify = async () => {
        setVerifying(true);
        await mockDelay(1400);
        setVerifying(false);
        setVerified(true);
    };

    return (
        <div>
            <div className="flex items-center justify-between">
                <p className="text-[12.5px] text-gray-500">{c.subheading}</p>
                <button
                    type="button"
                    onClick={copy}
                    className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1 text-[11.5px] font-medium text-gray-600 transition-colors hover:bg-gray-50">
                    {copied ? (
                        <Check className="h-3 w-3 text-emerald-600" strokeWidth={2.5} />
                    ) : (
                        <Copy className="h-3 w-3" strokeWidth={2} />
                    )}
                    {copied ? c.copied : c.copy}
                </button>
            </div>

            <pre className="mt-2.5 overflow-x-auto rounded-lg bg-gray-900 px-3.5 py-3 font-mono text-[11px] leading-relaxed text-gray-100">
                <code>{DEMO_SNIPPET}</code>
            </pre>

            {verified ? (
                <p className="mt-3.5 inline-flex items-center gap-2 text-[12.5px] font-medium text-emerald-700">
                    <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
                    {c.verified}
                </p>
            ) : (
                <button
                    type="button"
                    onClick={verify}
                    disabled={verifying}
                    className="mt-3.5 inline-flex items-center gap-2 rounded-lg bg-geekblue-600 px-3.5 py-2 text-[12.5px] font-medium text-white transition-colors hover:bg-geekblue-700 disabled:opacity-60">
                    {verifying && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                    {verifying ? c.verifying : c.verify}
                </button>
            )}
        </div>
    );
}

/**
 * Mirrors the group_id mapping step in the Revenue Intelligence Stripe setup:
 * a metadata key the customer stamps on the Stripe customer, which we read to
 * join a recorded visit to a recorded payment.
 */
const SESSION_ID_SNIPPET = `curl https://api.stripe.com/v1/customers \\
  -u "sk_live_...:" \\
  -d name="Jenny Rosen" \\
  -d "metadata[session_id]"=ts_9f2c41ab`;

export function RevenueStackSessionIdStep() {
    const c = COPY.revenueStack.sessionId;
    const [copied, setCopied] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [found, setFound] = useState(false);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(SESSION_ID_SNIPPET);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {
            /* clipboard blocked — the snippet is selectable on screen */
        }
    };

    const verify = async () => {
        setVerifying(true);
        await mockDelay(1400);
        setVerifying(false);
        setFound(true);
    };

    return (
        <div>
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-[12px] text-gray-500">{c.keyLabel}</span>
                <code className="rounded-md bg-gray-100 px-2 py-0.5 font-mono text-[12px] text-gray-800">
                    {c.keyValue}
                </code>
            </div>

            <div className="mt-3 flex items-center justify-between gap-3">
                <p className="text-[12px] text-gray-500">{c.snippetLabel}</p>
                <button
                    type="button"
                    onClick={copy}
                    className="inline-flex flex-none items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1 text-[11.5px] font-medium text-gray-600 transition-colors hover:bg-gray-50">
                    {copied ? (
                        <Check className="h-3 w-3 text-emerald-600" strokeWidth={2.5} />
                    ) : (
                        <Copy className="h-3 w-3" strokeWidth={2} />
                    )}
                    {copied ? c.copied : c.copy}
                </button>
            </div>

            <pre className="mt-2 overflow-x-auto rounded-lg bg-gray-900 px-3.5 py-3 font-mono text-[11px] leading-relaxed text-gray-100">
                <code>{SESSION_ID_SNIPPET}</code>
            </pre>

            <p className="mt-2.5 text-[11.5px] italic text-gray-400">{c.note}</p>

            {found ? (
                <p className="mt-3.5 inline-flex items-center gap-2 text-[12.5px] font-medium text-emerald-700">
                    <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
                    {c.found}
                </p>
            ) : (
                <button
                    type="button"
                    onClick={verify}
                    disabled={verifying}
                    className="mt-3.5 inline-flex items-center gap-2 rounded-lg bg-geekblue-600 px-3.5 py-2 text-[12.5px] font-medium text-white transition-colors hover:bg-geekblue-700 disabled:opacity-60">
                    {verifying && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                    {verifying ? c.verifying : c.verify}
                </button>
            )}
        </div>
    );
}

export function RevenueStackStripeStep() {
    const c = COPY.revenueStack.stripe;
    const [connecting, setConnecting] = useState(false);
    const [connected, setConnected] = useState(false);

    const connect = async () => {
        setConnecting(true);
        await mockDelay(1500);
        setConnecting(false);
        setConnected(true);
    };

    if (connected) {
        return (
            <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3.5">
                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-emerald-600" strokeWidth={2} />
                <div>
                    <p className="text-[13px] font-semibold text-emerald-900">{c.connected("ThriveStack Inc.")}</p>
                    <p className="mt-0.5 text-[12px] text-emerald-800">{c.scope}</p>
                </div>
            </div>
        );
    }

    return (
        <div>
            <button
                type="button"
                onClick={connect}
                disabled={connecting}
                className="inline-flex items-center gap-2.5 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-[13px] font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:opacity-60">
                {connecting ? (
                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                ) : (
                    <CreditCard className="h-4 w-4 text-[#635BFF]" strokeWidth={2} />
                )}
                {connecting ? c.connecting : c.connect}
            </button>
            <p className="mt-3 text-[12.5px] text-gray-500">{c.scope}</p>
        </div>
    );
}
